import { Building2, FileSpreadsheet, PenLine, Server, Store } from "lucide-react";
import type { ReactNode } from "react";
import { cx } from "@/components/ui";
import { AMAZON_PATH, KAUFLAND_PATH, SHOPWARE_PATH, WOOCOMMERCE_PATH } from "./brand-paths";

import { t, msg } from "@/lib/i18n";
/** Shopify-Markenzeichen (Pfad aus simple-icons, CC0) – nur zur Kennzeichnung der Anbindung */
const SHOPIFY_PATH =
  "M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z";

/** Markenzeichen (simple-icons-Pfad, unverändert) in Markenfarbe auf getönter Kachel – wie bei Shopify */
function brand(label: string, path: string, color: string, tile: string): SourceStyle {
  return { label, tile, icon: (cls) => <svg viewBox="0 0 24 24" className={cls} aria-hidden="true"><path fill={color} d={path} /></svg> };
}

interface SourceStyle {
  label: string;
  /** Hintergrund der Kachel */
  tile: string;
  icon: (cls: string) => ReactNode;
}

/**
 * Logo/Symbol je Anbindungsart. Neue Quellen (WooCommerce, Shopware, Amazon …) hier ergänzen –
 * unbekannte Arten bekommen ein neutrales Shop-Symbol.
 */
const SOURCES: Record<string, SourceStyle> = {
  shopify: {
    label: msg("Shopify"),
    tile: "bg-[#95BF47]/15",
    icon: (cls) => (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true"><path fill="#95BF47" d={SHOPIFY_PATH} /></svg>
    ),
  },
  sftp: { label: msg("SFTP"), tile: "bg-surface-2", icon: (cls) => <Server className={cx(cls, "text-fg-muted")} /> },
  csv: { label: msg("CSV-Upload"), tile: "bg-surface-2", icon: (cls) => <FileSpreadsheet className={cx(cls, "text-fg-muted")} /> },
  manual: { label: msg("Manuell"), tile: "bg-surface-2", icon: (cls) => <PenLine className={cx(cls, "text-fg-muted")} /> },
  woocommerce: brand("WooCommerce", WOOCOMMERCE_PATH, "#7F54B3", "bg-[#7F54B3]/15"),
  shopware: brand("Shopware", SHOPWARE_PATH, "#189EFF", "bg-[#189EFF]/15"),
  kaufland: brand("Kaufland", KAUFLAND_PATH, "#E10915", "bg-[#E10915]/12"),
  amazon: brand("Amazon", AMAZON_PATH, "#FF9900", "bg-[#FF9900]/15"),
  // ERP: neutrales Symbol (kein nachgebautes Markenzeichen)
  odoo: { label: msg("Odoo"), tile: "bg-[#714B67]/12", icon: (cls) => <Building2 className={cx(cls, "text-[#714B67]")} /> },
};

const FALLBACK: SourceStyle = { label: msg("Shop"), tile: "bg-surface-2", icon: (cls) => <Store className={cx(cls, "text-fg-muted")} /> };

export const channelSource = (kind: string) => SOURCES[kind] ?? FALLBACK;

/** Kleines Kanal-Logo im Text (z. B. Auftragsliste), Name der Anbindung im Tooltip */
export function ChannelMark({ kind, className }: { kind: string | null | undefined; className?: string }) {
  if (!kind) return null;
  const s = channelSource(kind);
  return <span className={cx("inline-flex shrink-0", className)} title={t(s.label)} role="img" aria-label={t(s.label)}>{s.icon("size-3.5")}</span>;
}

export function ChannelLogo({ kind, size = "md" }: { kind: string; size?: "sm" | "md" | "lg" }) {
  const s = channelSource(kind);
  const box = size === "lg" ? "size-12 rounded-2xl" : size === "sm" ? "size-8 rounded-lg" : "size-10 rounded-xl";
  const ico = size === "lg" ? "size-6" : size === "sm" ? "size-4" : "size-5";
  return (
    <div className={cx(box, s.tile, "grid place-items-center shrink-0")} title={t(s.label)}>
      {s.icon(ico)}
    </div>
  );
}
